import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, ChevronDown, ChevronLeft, ChevronRight, Crown, RefreshCw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Common";
import { PlanDay, PlanProgress, StreakPills } from "@/components/plan/PlanParts";
import { askUpgrade, todayIndex, usePaidPlan, useWeekPlan } from "@/lib/weekPlan";
import { cn } from "@/lib/utils";

const OPEN_KEY = "rpopro_today_plan_open";

const Upsell = () => (
  <div className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-[#D4AF37]/30 bg-[#33290D]/40 p-4" data-testid="today-plan-upsell">
    <CalendarCheck className="h-5 w-5 text-[#D4AF37]" />
    <p className="flex-1 text-sm text-zinc-300">Plan your week with AI, tick off tasks and build a streak.</p>
    <Button size="sm" onClick={askUpgrade} data-testid="today-plan-upgrade-button"><Crown className="h-4 w-4" />Unlock with Pro</Button>
  </div>
);

export const TodayPlanCard = () => {
  const paid = usePaidPlan();
  const { plan, busy, toggle, replan } = useWeekPlan(paid);
  const [open, setOpen] = useState(() => localStorage.getItem(OPEN_KEY) !== "0");
  const sections = plan?.result?.sections || [];
  const [idx, setIdx] = useState(null);
  useEffect(() => { if (plan?.created_at) setIdx(Math.max(0, todayIndex(plan.result?.sections || []))); }, [plan?.created_at]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!paid) return <Upsell />;
  if (plan === undefined) return <div className="mb-6"><Spinner className="text-[#D4AF37]" /></div>;

  const flip = (o) => { localStorage.setItem(OPEN_KEY, o ? "1" : "0"); setOpen(o); };
  const done = new Set(plan?.progress?.done || []);
  const cur = idx ?? 0;
  const tIdx = todayIndex(sections);

  return (
    <div className="mb-6 rounded-2xl border border-[#D4AF37]/25 bg-[#121214]/90 p-4 md:p-5" data-testid="today-plan-card">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => flip(!open)} className="flex items-center gap-2 font-display text-lg text-zinc-100" data-testid="today-plan-toggle">
          <CalendarCheck className="h-5 w-5 text-[#D4AF37]" />Today's plan<ChevronDown className={cn("h-4 w-4 text-zinc-500 transition-transform", !open && "-rotate-90")} />
        </button>
        {plan && <StreakPills streak={plan.progress?.streak} />}
        <div className="ml-auto flex items-center gap-2">
          <Link to="/calendar" className="text-xs text-zinc-400 hover:text-[#F3C649]" data-testid="today-plan-week-link">Full week</Link>
          <Button size="sm" variant="outline" onClick={replan} disabled={busy} data-testid="today-plan-replan-button">{busy ? <Spinner /> : plan ? <><RefreshCw className="h-3.5 w-3.5" />Re-plan</> : <><Sparkles className="h-3.5 w-3.5" />Plan my week</>}</Button>
        </div>
      </div>
      {open && (plan ? (
        <div className="mt-4 space-y-4">
          <PlanProgress progress={plan.progress} />
          <div className="flex items-center gap-2">
            <Button size="icon" variant="ghost" disabled={cur <= 0} onClick={() => setIdx(cur - 1)} aria-label="Previous day" data-testid="today-plan-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <div className="min-w-0 flex-1">{sections[cur] && <PlanDay section={sections[cur]} si={cur} done={done} onToggle={toggle} today={cur === tIdx} />}</div>
            <Button size="icon" variant="ghost" disabled={cur >= sections.length - 1} onClick={() => setIdx(cur + 1)} aria-label="Next day" data-testid="today-plan-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      ) : <p className="mt-3 text-sm text-zinc-500" data-testid="today-plan-empty">No plan yet. Tap "Plan my week" and your AI coach will lay out the next 7 days.</p>)}
    </div>
  );
};
